import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { LabelService } from '../../../services/label.service';
import { MatChipListboxChange } from '@angular/material/chips';
import {
  OpzioniTipoFattura,
  TableInvoiceFields,
  TableReceivedInvoiceFields,
} from '../../../models/invoices';
import { FormControl, FormGroup } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { InvoicesService } from '../../../services/invoices.service';
import { Router } from '@angular/router';
@Component({
  selector: 'app-table-received-invoices',
  standalone: false,
  templateUrl: './table-received-invoices.component.html',
  styleUrl: './table-received-invoices.component.scss',
})
export class TableReceivedInvoicesComponent implements OnChanges, OnInit {
  @Input() invoices: TableReceivedInvoiceFields[] = [];
  @Input() isLoadingInvoices: boolean = true;

  filteredInvoices: TableReceivedInvoiceFields[] = [];

  selectedChips: string[] = ['all'];
  currentYear = new Date().getFullYear();
  showDatePicker = false;
  showClientSearch = false;
  clientSearchValue = '';

  selectedInvoices: Set<TableReceivedInvoiceFields> = new Set();
  selectedSDIInvoices: TableReceivedInvoiceFields[] = [];

  dateRange = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  displayedColumns: string[] = [
    'select',
    'number',
    'year',
    'status',
    'date',
    'type',
    'sender',
    'total',
    'pagata',
    'actions',
  ];

  hasConfirmedRegistration = false;

  constructor(
    public labelService: LabelService,
    private invoicesService: InvoicesService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoices']) {
      this.filteredInvoices = this.invoices;
      this.selectedChips = ['all'];
      this.filterInvoicesByChips();
    }
  }

  ngOnInit(): void {
    this.invoicesService.getCodiceUnivocoRegisteredInADEFlag().subscribe({
      next: (response: boolean) => {
        this.hasConfirmedRegistration = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error(
          'Error getting codice univoco registered in ADE flag',
          error
        );
      },
    });
  }

  onChipSelectionChange(event: MatChipListboxChange): void {
    const value = event.value as string[];
    this.selectedInvoices.clear();
    this.updateSelectedInvoicesArrays();

    // If no chips are selected, select 'all'
    if (value.length === 0) {
      this.selectedChips = ['all'];
      this.filterInvoicesByChips();
      return;
    }

    if (value.length > 0) {
      if (value.includes('all')) {
        this.selectedChips = value.filter((chip) => chip !== 'all');
      } else {
        this.selectedChips = value;
      }
      this.filterInvoicesByChips();
      return;
    }
  }

  private filterInvoicesByChips(): void {
    if (this.selectedChips.includes('all')) {
      this.filteredInvoices = this.invoices;
      return;
    }

    this.filteredInvoices = this.invoices.filter((invoice) => {
      return this.selectedChips.every((chip) => {
        switch (chip) {
          case 'company':
            return invoice.tipo === OpzioniTipoFattura.PARCELLA_SDI;
          case 'credit_note_sdi':
            return invoice.tipo === OpzioniTipoFattura.NOTA_CREDITO_SDI;
          case 'year':
            return (
              invoice.progressivo_anno.toString() ===
              this.currentYear.toString()
            );
          case 'date_range':
            const start = this.dateRange.get('start')?.value;
            const end = this.dateRange.get('end')?.value;
            if (!start || !end) return true;
            const [day, month, year] = invoice.data.split('/').map(Number);
            const invoiceDate = new Date(year, month - 1, day);
            return invoiceDate >= start && invoiceDate <= end;
          default:
            return true;
        }
      });
    });
  }

  toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
    if (!this.showDatePicker) {
      this.dateRange.reset();
      this.onChipSelectionChange({
        value: this.selectedChips,
      } as MatChipListboxChange);
    }
  }

  onDateRangeChange(): void {
    const start = this.dateRange.get('start')?.value;
    const end = this.dateRange.get('end')?.value;

    if (start && end) {
      this.filteredInvoices = this.invoices.filter((invoice) => {
        const [day, month, year] = invoice.data.split('/').map(Number);
        const invoiceDate = new Date(year, month - 1, day);
        return invoiceDate >= start && invoiceDate <= end;
      });
    }
  }

  onSort(sortState: Sort) {
    if (sortState.direction === '') {
      // If direction is empty, reset to original data
      this.filteredInvoices = [...this.invoices];
      return;
    }

    this.filteredInvoices = [...this.invoices].sort((a, b) => {
      const isAsc = sortState.direction === 'asc';
      switch (sortState.active) {
        case 'progressivo_numero':
          return this.compare(
            a.progressivo_numero,
            b.progressivo_numero,
            isAsc
          );
        case 'progressivo_anno':
          return this.compare(a.progressivo_anno, b.progressivo_anno, isAsc);
        case 'data':
          return this.compare(
            this.parseDate(a.data),
            this.parseDate(b.data),
            isAsc
          );
        default:
          return 0;
      }
    });
  }
  private compare(a: any, b: any, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  toggleClientSearch(): void {
    this.showClientSearch = !this.showClientSearch;
    if (!this.showClientSearch) {
      this.clientSearchValue = '';
      this.filteredInvoices = this.invoices;
    }
  }

  onClientSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.clientSearchValue = value;

    if (!value.trim()) {
      this.filteredInvoices = this.invoices;
      return;
    }

    this.filteredInvoices = this.invoices.filter((invoice) =>
      invoice.mittente.toLowerCase().includes(value.toLowerCase())
    );
  }

  clearSearch(): void {
    this.clientSearchValue = '';
    this.filteredInvoices = this.invoices;
  }

  onPagataChange(
    invoice: TableReceivedInvoiceFields,
    event: MatCheckboxChange
  ): void {
    // this.pagataChanged.emit({invoice, checked: event.checked});
  }

  showNotifications(invoice: TableInvoiceFields): void {
    // this.showedNotifications.emit(invoice);
  }

  previewInvoice(invoice: TableInvoiceFields): void {
    // this.previewedInvoice.emit(invoice);
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selectedInvoices.clear();
    } else {
      this.filteredInvoices.forEach((row) => this.selectedInvoices.add(row));
    }
    this.updateSelectedInvoicesArrays();
  }

  isAllSelected() {
    const numSelected = this.selectedInvoices.size;
    const numRows = this.filteredInvoices.length;
    return numSelected === numRows && numRows > 0;
  }

  isIndeterminate() {
    return !this.isAllSelected() && this.selectedInvoices.size > 0;
  }
  private updateSelectedInvoicesArrays(): void {
    this.selectedSDIInvoices = Array.from(this.selectedInvoices).filter(
      (inv) =>
        inv.tipo === OpzioniTipoFattura.PARCELLA_SDI ||
        inv.tipo === OpzioniTipoFattura.NOTA_CREDITO_SDI
    );
  }

  onRowSelection(invoice: TableReceivedInvoiceFields): void {
    if (this.selectedInvoices.has(invoice)) {
      this.selectedInvoices.delete(invoice);
    } else {
      this.selectedInvoices.add(invoice);
    }
    this.updateSelectedInvoicesArrays();
  }

  isSelected(invoice: TableReceivedInvoiceFields): boolean {
    return this.selectedInvoices.has(invoice);
  }

  onRegistrationConfirmed(event: MatCheckboxChange): void {
    this.hasConfirmedRegistration = event.checked;
    this.invoicesService
      .setCodiceUnivocoRegisteredInADEFlag(this.hasConfirmedRegistration)
      .subscribe({
        next: () => {
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error(
            'Error setting codice univoco registered in ADE flag',
            error
          );
        },
      });
  }

  openFAQ(): void {
    this.router.navigate(['/faq'], {
      fragment:
        'fatturazione--come-registro-il-codice-destinatario-di-acube-sul-sito-dell-agenzia-delle-entrate',
    });
  }
}
